import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { publicAPI } from '../utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShareButtons } from '@/components/ShareJobButtons';
import {
  Building2, MapPin, Phone, Globe, Briefcase, Loader2, AlertCircle, Star,
  ShieldCheck, Calendar, ArrowRight, Search, Mail, CheckCircle2, Clock, Award
} from 'lucide-react';

export default function EmployerProfile() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const EMPLOYMENT_LABEL = {
    'full-time': t('jobDetails.employment.full-time'),
    'part-time': t('jobDetails.employment.part-time'),
    'contract': t('jobDetails.employment.contract'),
    'internship': t('jobDetails.employment.internship'),
    'temporary': t('jobDetails.employment.temporary')
  };

  const formatSalary = (j) => {
    if (j.salary_range_min && j.salary_range_max)
      return `${Number(j.salary_range_min).toLocaleString('pt-PT')} – ${Number(j.salary_range_max).toLocaleString('pt-PT')} ${j.currency || 'CVE'}`;
    if (j.salary_range_min)
      return t('employerProfile.salaryFrom', { amount: Number(j.salary_range_min).toLocaleString('pt-PT'), currency: j.currency || 'CVE' });
    return null;
  };

  useEffect(() => {
    publicAPI.getEmployer(slug)
      .then((r) => setData(r.data))
      .catch(() => setError(t('employerProfile.notFoundTitle')))
      .finally(() => setLoading(false));
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive sector from most common category across active jobs
  const sector = useMemo(() => {
    if (!data?.jobs?.length) return null;
    const counts = {};
    data.jobs.forEach(j => { if (j.category) counts[j.category] = (counts[j.category] || 0) + 1; });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return sorted[0]?.[0] || null;
  }, [data]);


  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-blue-500" aria-label={t('employerProfile.loading')} />
      </main>
    );
  }

  if (error || !data) {
    return (
      <main className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <Card className="p-10 text-center max-w-md border-slate-200">
          <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
          <h1 className="text-xl font-semibold text-slate-900 mb-1">{error || t('employerProfile.notFoundTitle')}</h1>
          <p className="text-slate-500 text-sm mb-5">{t('employerProfile.notFoundDesc')}</p>
          <Button onClick={() => navigate('/jobs/search')} className="bg-slate-900 hover:bg-slate-800">
            <Search className="h-4 w-4 mr-2" />{t('employerProfile.searchJobsBtn')}
          </Button>
        </Card>
      </main>
    );
  }

  const name = data.company_name || t('employerProfile.defaultCompanyName');
  const jobsWord = t('employerProfile.vaga', { count: data.active_jobs_count });
  const pageTitle = t('employerProfile.seoTitle', { name });
  const pageDesc = data.company_description
    ? t('employerProfile.seoDescWithDesc', { name, count: data.active_jobs_count, jobsWord, description: data.company_description }).slice(0, 240)
    : t('employerProfile.seoDescNoDesc', { name, count: data.active_jobs_count, jobsWord, locationPart: data.location ? ` ${t('employerProfile.localPresenceIn', { location: data.location })}` : '' });
  const canonical = `https://trabadju.cv/empresa/${data.slug}`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="profile" />
        <meta property="og:site_name" content="Trabadju" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonical} />
        {data.logo_url && <meta property="og:image" content={data.logo_url} />}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        {/* Organization JSON-LD for richer SEO */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": name,
          "url": canonical,
          ...(data.logo_url ? { logo: data.logo_url } : {}),
          ...(data.website ? { sameAs: [data.website] } : {}),
          ...(data.location ? { address: { "@type": "PostalAddress", addressLocality: data.location, addressCountry: "CV" } } : {})
        })}</script>
      </Helmet>

      <main className="min-h-screen bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
          {/* Hero card */}
          <header className="relative bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden mb-6" data-testid="employer-profile-header">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-600 via-indigo-500 to-emerald-500" aria-hidden="true" />
            <div className="p-5 sm:p-7">
              <div className="flex flex-col sm:flex-row sm:items-start gap-5">
                {/* Logo */}
                <div className="shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center border border-slate-200 overflow-hidden">
                  {data.logo_url ? (
                    <img src={data.logo_url} alt={t('employerProfile.logoAlt', { name })} className="w-full h-full object-cover" />
                  ) : (
                    <Building2 className="h-10 w-10 text-blue-600" aria-hidden="true" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  {/* Trust badges */}
                  <div className="flex flex-wrap items-center gap-1.5 mb-2">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-widest bg-blue-50 text-blue-700 border border-blue-100">
                      <ShieldCheck className="h-3 w-3" aria-hidden="true" />{t('employerProfile.trustEmployer')}
                    </span>
                    {data.nif_verified && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-widest bg-emerald-600 text-white" data-testid="employer-verified-badge">
                        <CheckCircle2 className="h-3 w-3" aria-hidden="true" />{t('employerProfile.nifVerified')}
                      </span>
                    )}
                    {data.is_trusted && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-widest bg-amber-400 text-slate-900" title={t('employerProfile.trustedDesc')} data-testid="employer-trusted-badge">
                        <Award className="h-3 w-3" aria-hidden="true" />{t('employerProfile.trustedBadge')}
                      </span>
                    )}
                    {data.active_jobs_count > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-widest bg-emerald-50 text-emerald-700 border border-emerald-100">
                        <Briefcase className="h-3 w-3" aria-hidden="true" />{data.active_jobs_count} {t('employerProfile.activeJob', { count: data.active_jobs_count })}
                      </span>
                    )}
                  </div>

                  <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight leading-tight" data-testid="employer-name">{name}</h1>

                  {/* Meta line */}
                  <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-2.5 text-[13.5px] text-slate-600">
                    {data.location && (
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />{data.location}
                      </span>
                    )}
                    {sector && (
                      <span className="inline-flex items-center gap-1.5">
                        <Briefcase className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />{sector}
                      </span>
                    )}
                    {data.website && (
                      <a href={data.website.startsWith('http') ? data.website : `https://${data.website}`} target="_blank" rel="noopener noreferrer"
                         className="inline-flex items-center gap-1.5 text-blue-700 hover:text-blue-900 hover:underline">
                        <Globe className="h-3.5 w-3.5" aria-hidden="true" />{t('employerProfile.website')}
                      </a>
                    )}
                    {data.phone && (
                      <a href={`tel:${data.phone}`} className="inline-flex items-center gap-1.5 hover:text-slate-900">
                        <Phone className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />{data.phone}
                      </a>
                    )}
                  </div>

                  {/* CTA + share */}
                  <div className="flex flex-wrap items-center gap-2 mt-5">
                    {data.active_jobs_count > 0 ? (
                      <Button
                        onClick={() => document.getElementById('jobs-section')?.scrollIntoView({ behavior: 'smooth' })}
                        className="bg-slate-900 hover:bg-slate-800 text-white font-semibold shadow-sm"
                        data-testid="see-jobs-btn"
                      >
                        <Briefcase className="h-4 w-4 mr-2" />{t('employerProfile.seeJobsBtn')}
                      </Button>
                    ) : (
                      <Button onClick={() => navigate('/jobs/search')} className="bg-slate-900 hover:bg-slate-800 font-semibold">
                        <Search className="h-4 w-4 mr-2" />{t('employerProfile.searchOtherJobsBtn')}
                      </Button>
                    )}
                  </div>
                  <ShareButtons
                    url={`${window.location.origin}/empresa/${slug}`}
                    title={`${data.company_name} · Trabadju.cv`}
                    text={t('employerProfile.shareText', { company: data.company_name, count: (data.jobs || []).length })}
                    testId="share-employer"
                  />
                </div>
              </div>
            </div>
          </header>

          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {/* Sobre */}
              <Card className="p-6 sm:p-7 border-slate-200" data-testid="employer-about">
                <h2 className="text-[15px] font-bold uppercase tracking-[0.08em] text-slate-500 mb-3">{t('employerProfile.aboutTitle')}</h2>
                {data.company_description ? (
                  <p className="text-[15px] text-slate-800 leading-[1.7] whitespace-pre-wrap">{data.company_description}</p>
                ) : (
                  <div className="text-[14px] text-slate-600 bg-slate-50 border border-slate-100 rounded-xl p-4 leading-snug">
                    <p className="font-semibold text-slate-800 mb-1">{t('employerProfile.noDescription', { name })}</p>
                    <p>{t('employerProfile.noDescriptionHint')}</p>
                  </div>
                )}
              </Card>

              {/* Active jobs */}
              <section id="jobs-section" aria-labelledby="jobs-title">
                <div className="flex items-end justify-between mb-3 gap-3">
                  <h2 id="jobs-title" className="text-[15px] font-bold uppercase tracking-[0.08em] text-slate-500">{t('employerProfile.activeJobsTitle')} {data.active_jobs_count > 0 && <span className="text-slate-400 normal-case font-normal">({data.active_jobs_count})</span>}</h2>
                  <Link to="/jobs/search" className="text-[13px] font-semibold text-blue-700 hover:text-blue-900 inline-flex items-center gap-1">
                    {t('employerProfile.seeMore')} <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </Link>
                </div>

                {data.jobs.length === 0 ? (
                  <Card className="p-8 text-center border-dashed border-slate-300" data-testid="employer-no-jobs">
                    <Briefcase className="h-10 w-10 text-slate-400 mx-auto mb-3" />
                    <p className="text-slate-800 font-semibold mb-1">{t('employerProfile.noActiveJobsTitle')}</p>
                    <p className="text-slate-500 text-[13px] mb-5">{t('employerProfile.noActiveJobsDesc', { name })}</p>
                    <Button onClick={() => navigate('/jobs/search')} className="bg-slate-900 hover:bg-slate-800">
                      <Search className="h-4 w-4 mr-2" />{t('employerProfile.searchOtherJobsBtn')}
                    </Button>
                  </Card>
                ) : (
                  <ul className="space-y-3" data-testid="employer-jobs-list">
                    {data.jobs.map((j) => (
                      <li key={j.id} className="contents">
                        <Link to={`/jobs/${j.id}`} className="block" data-testid={`employer-job-${j.id}`}>
                          <Card className="p-5 hover:shadow-md hover:border-slate-300 border-slate-200 transition-all cursor-pointer group">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                                  {j.is_featured && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold border border-amber-200 bg-amber-50 text-amber-800">
                                      <Star className="h-3 w-3 fill-amber-500 text-amber-500" aria-hidden="true" />{t('employerProfile.featured')}
                                    </span>
                                  )}
                                </div>
                                <h3 className="text-[16px] font-bold text-slate-900 group-hover:text-blue-700 transition-colors leading-snug truncate">{j.title_pt}</h3>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-slate-600 mt-1">
                                  {j.location && (
                                    <span className="inline-flex items-center gap-1">
                                      <MapPin className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />{j.location}
                                    </span>
                                  )}
                                  {j.employment_type && (
                                    <span className="inline-flex items-center gap-1">
                                      <Clock className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />{EMPLOYMENT_LABEL[j.employment_type] || j.employment_type}
                                    </span>
                                  )}
                                  {j.category && <span className="text-slate-500">· {j.category}</span>}
                                </div>
                                {formatSalary(j) && (
                                  <p className="mt-2 text-[13.5px] font-semibold text-emerald-700">{formatSalary(j)}</p>
                                )}
                              </div>
                              <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-slate-900 transition mt-1 flex-none" aria-hidden="true" />
                            </div>
                          </Card>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>

            {/* Sidebar: Trust + info */}
            <aside className="space-y-4">
              <Card className="p-5 border-slate-200" data-testid="employer-trust">
                <h2 className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-3">{t('employerProfile.trustSidebarTitle')}</h2>
                <ul className="space-y-2.5 text-[13.5px]">
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-none mt-0.5" aria-hidden="true" />
                    <span className="text-slate-700"><strong className="text-slate-900">{t('employerProfile.verifiedAccount')}</strong> {t('employerProfile.verifiedAccountDesc')}</span>
                  </li>
                  {data.is_trusted && (
                    <li className="flex items-start gap-2.5" data-testid="employer-trusted-sidebar">
                      <Award className="h-4 w-4 text-amber-500 flex-none mt-0.5" aria-hidden="true" />
                      <span className="text-slate-700"><strong className="text-slate-900">{t('employerProfile.trustedBadge')}.</strong> {t('employerProfile.trustedDesc')}</span>
                    </li>
                  )}
                  {data.location && (
                    <li className="flex items-start gap-2.5">
                      <MapPin className="h-4 w-4 text-blue-600 flex-none mt-0.5" aria-hidden="true" />
                      <span className="text-slate-700"><strong className="text-slate-900">{t('employerProfile.localPresence')}</strong> {t('employerProfile.localPresenceIn', { location: data.location })}</span>
                    </li>
                  )}
                  {data.active_jobs_count > 0 && (
                    <li className="flex items-start gap-2.5">
                      <Briefcase className="h-4 w-4 text-blue-600 flex-none mt-0.5" aria-hidden="true" />
                      <span className="text-slate-700">{data.active_jobs_count} {t('employerProfile.recruitingNow', { count: data.active_jobs_count })}</span>
                    </li>
                  )}
                  <li className="flex items-start gap-2.5">
                    <Calendar className="h-4 w-4 text-slate-500 flex-none mt-0.5" aria-hidden="true" />
                    <span className="text-slate-700">{t('employerProfile.directApplications')}</span>
                  </li>
                </ul>
              </Card>

              <Card className="p-5 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100">
                <h3 className="font-semibold text-slate-900 text-[14px] mb-1">{t('employerProfile.candidateCtaTitle')}</h3>
                <p className="text-[13px] text-slate-700 leading-snug mb-3">{t('employerProfile.candidateCtaDesc')}</p>
                <Button onClick={() => navigate('/jobs/search')} variant="outline" size="sm" className="w-full border-slate-300 bg-white" data-testid="cta-search">
                  <Search className="h-3.5 w-3.5 mr-1.5" />{t('employerProfile.searchMoreBtn')}
                </Button>
              </Card>

              <Card className="p-5 border-slate-200 text-[12.5px] text-slate-500 leading-snug">
                <p>{t('employerProfile.disclaimerPre')} <a href="mailto:info@trabadju.cv" className="text-blue-700 hover:underline inline-flex items-center gap-1"><Mail className="h-3 w-3" />info@trabadju.cv</a>.</p>
              </Card>
            </aside>
          </div>
        </div>
      </main>
    </>
  );
}
